import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type CelebrationType = 'achievement' | 'levelUp' | 'streak';

export interface Celebration {
  id: string;
  type: CelebrationType;
  title: string;
  description: string;
  icon: string;
}

interface CelebrationContextType {
  currentCelebration: Celebration | null;
  celebrate: (celebration: Omit<Celebration, 'id'>) => void;
  dismissCelebration: () => void;
}

const CelebrationContext = createContext<CelebrationContextType | undefined>(undefined);

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Celebration[]>([]);
  const [currentCelebration, setCurrentCelebration] = useState<Celebration | null>(null);

  const processQueue = useCallback(() => {
    setQueue((prevQueue) => {
      if (prevQueue.length > 0) {
        const [next, ...rest] = prevQueue;
        setCurrentCelebration(next);
        return rest;
      }
      return prevQueue;
    });
  }, []);

  const celebrate = useCallback((celebration: Omit<Celebration, 'id'>) => {
    const newCelebration: Celebration = {
      ...celebration,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };

    if (!currentCelebration) {
      setCurrentCelebration(newCelebration);
    } else {
      setQueue((prev) => [...prev, newCelebration]);
    }
  }, [currentCelebration]);

  const dismissCelebration = useCallback(() => {
    setCurrentCelebration(null);
    // Process next in queue after a short delay
    setTimeout(processQueue, 300);
  }, [processQueue]);

  return (
    <CelebrationContext.Provider value={{ currentCelebration, celebrate, dismissCelebration }}>
      {children}
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (context === undefined) {
    throw new Error('useCelebration must be used within a CelebrationProvider');
  }
  return context;
}
